/** W73c (D-283, Befund 7): Bildschirmtastatur. Safari auf dem iPhone verkleinert bei offener Tastatur nur den sichtbaren
 *  Ausschnitt (`visualViewport`), feste Leisten bleiben am unteren Rand des Fensters und stehen dann mitten im Bild; nach
 *  dem Schliessen bleibt die Seite versetzt. Chrome auf Android verkleinert mit `interactive-widget=resizes-content`
 *  (index.html) das ganze Fenster. Beides gilt hier als offene Tastatur, solange ein Eingabefeld den Fokus hat:
 *  `html[data-tastatur="offen"]` blendet die Tab-Leiste aus, `--tastatur-unten` richtet die Eingabe des Gaists am
 *  sichtbaren Ausschnitt aus (bausteine.css); das Feld wird in den sichtbaren Bereich geholt, beim Schliessen kommt der
 *  Scrollstand von vorher zurueck. W73d: `--sicht-hoehe` (Hoehe des Ausschnitts) stellt das mobile Blatt in den Ausschnitt. */

/** So viel kleiner als das Fenster muss der sichtbare Ausschnitt sein (px), damit er als Tastatur zaehlt; kleinere
 *  Unterschiede sind Adressleiste und Werkzeugleiste des Browsers. */
export const TASTATUR_SCHWELLE = 150;

/** Offen, wenn der sichtbare Ausschnitt deutlich kleiner ist als das Fenster. `fenster` ist die groesste Fensterhoehe bei
 *  gleicher Breite: Chrome auf Android verkleinert auch `innerHeight` (resizes-content), Safari nur den Ausschnitt. */
export function tastaturOffen(sicht: number, fenster: number, schwelle = TASTATUR_SCHWELLE): boolean {
  return sicht > 0 && sicht < fenster - schwelle;
}

const OHNE_TASTATUR = new Set(["button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit"]);

/** Feld, das eine Bildschirmtastatur oeffnet (Text, Suche, Zahl, Datum zaehlt mit: Safari zeigt dort ein Rad unten). */
export function istEingabe(el: Element | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return !OHNE_TASTATUR.has(el.type) && !el.readOnly && !el.disabled;
  return el.isContentEditable === true;
}

/** Abstand vom unteren Fensterrand bis zum unteren Rand des sichtbaren Ausschnitts (Hoehe der Tastatur ueber dem Fenster). */
export function tastaturUnten(fenster: number, sicht: number, oben: number): number {
  return Math.max(0, Math.round(fenster - sicht - oben));
}

/** Stand vor dem Fokus: Scrollhoehe und Seite. Der Gaist-Chat haelt sich selbst unten, solange man unten war
 *  (AssistentChat, `warUnten`), auch nach dem Schliessen. */
type Stand = { y: number; pfad: string };

/** Beobachtet Fokus und sichtbaren Ausschnitt; gibt die Aufraeumfunktion zurueck. Einmal je App (Schale). */
export function tastaturBeobachten(): () => void {
  const wurzel = document.documentElement;
  const vv = window.visualViewport;
  let breite = window.innerWidth;
  let fenster = window.innerHeight;
  let offen = false;
  let stand: Stand | null = null;
  let nachlauf = 0; // bis wann ein Nachlauf des Schliessens den Stand noch einmal setzt (Safari scrollt selbst nach)
  let ziel: Stand | null = null;
  let takt = 0;

  const merken = (): Stand => ({ y: window.scrollY, pfad: location.pathname });
  const zuruecksetzen = (s: Stand) => {
    if (s.pfad === location.pathname) window.scrollTo(window.scrollX, s.y);
  };

  function messen(nachGroesse: boolean) {
    if (window.innerWidth !== breite) {
      // Drehen: neue Grundhoehe
      breite = window.innerWidth;
      fenster = window.innerHeight;
    } else fenster = Math.max(fenster, window.innerHeight);
    const sicht = vv ? vv.height * vv.scale : window.innerHeight;
    const feld = document.activeElement;
    const jetzt = istEingabe(feld) && tastaturOffen(sicht, fenster);
    wurzel.style.setProperty("--tastatur-unten", `${jetzt && vv ? tastaturUnten(window.innerHeight, vv.height, vv.offsetTop) : 0}px`);
    // W73d (HB1, D-363): Hoehe des Ausschnitts, solange die Tastatur offen ist; das mobile Blatt passt sich ihr an
    // (bausteine.css), damit sein Kopf nicht ueber dem Ausschnitt liegt
    if (jetzt) wurzel.style.setProperty("--sicht-hoehe", `${Math.round(vv ? vv.height : window.innerHeight)}px`);
    else wurzel.style.removeProperty("--sicht-hoehe");
    if (jetzt !== offen) {
      offen = jetzt;
      if (offen) {
        wurzel.dataset.tastatur = "offen";
        // das Feld bleibt sichtbar; ein festes Feld (Gaist) steht schon ueber der Tastatur
        if (istEingabe(feld)) feld.scrollIntoView?.({ block: "nearest" });
      } else {
        delete wurzel.dataset.tastatur;
        if (stand) {
          ziel = stand;
          zuruecksetzen(stand);
          nachlauf = performance.now() + 1000;
        }
        stand = null;
      }
    } else if (!offen && nachGroesse && ziel && performance.now() < nachlauf) zuruecksetzen(ziel);
    // Fokus ohne Tastatur (Rechner, Hardware-Tastatur): der Stand verfaellt mit dem Fokus
    if (!offen && !istEingabe(feld)) stand = null;
    window.dispatchEvent(new Event("pg-tastatur"));
  }

  const spaeter = () => {
    cancelAnimationFrame(takt);
    takt = requestAnimationFrame(() => messen(false));
  };
  function fokusRein(e: FocusEvent) {
    if (istEingabe(e.target as Element)) {
      // ein neues Feld beendet den Nachlauf des letzten Schliessens: sonst sprange die Seite bei einer Zwischenhoehe der
      // aufgehenden Tastatur zum alten Stand zurueck, weg vom gerade angetippten Feld (Verifier V73c R1)
      nachlauf = 0;
      // der Stand vor der Tastatur; beim Wechsel von Feld zu Feld (Tastatur bleibt offen) gilt der erste
      if (!offen && !stand) stand = merken();
    }
    spaeter();
  }
  function fokusRaus() {
    // erst im naechsten Takt: beim Wechsel zwischen zwei Feldern steht der Fokus kurz auf body
    spaeter();
  }
  // R1 W82-Web-5h-N2a (D-690): rollt eine Seite bei offener Tastatur selbst an eine neue Lage (Gaist-Chat: die gesendete Frage
  // oben), gilt diese beim Schließen als Stand; sonst kehrte die Seite zur Lage vor dem Fokus zurück, weg von der Frage
  function standNeu(e: Event) {
    const y = (e as CustomEvent<number>).detail;
    if (stand && typeof y === "number") stand = { y, pfad: location.pathname };
  }
  const groesse = () => messen(true);
  const verschoben = () => messen(false);

  window.addEventListener("focusin", fokusRein);
  window.addEventListener("focusout", fokusRaus);
  window.addEventListener("pg-tastatur-stand", standNeu);
  window.addEventListener("resize", groesse);
  vv?.addEventListener("resize", groesse);
  vv?.addEventListener("scroll", verschoben);
  return () => {
    cancelAnimationFrame(takt);
    window.removeEventListener("focusin", fokusRein);
    window.removeEventListener("focusout", fokusRaus);
    window.removeEventListener("pg-tastatur-stand", standNeu);
    window.removeEventListener("resize", groesse);
    vv?.removeEventListener("resize", groesse);
    vv?.removeEventListener("scroll", verschoben);
    delete wurzel.dataset.tastatur;
    wurzel.style.removeProperty("--tastatur-unten");
    wurzel.style.removeProperty("--sicht-hoehe");
  };
}
